import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Calendar, DollarSign, ShieldCheck, Layers } from "lucide-react";
import { MATERIALS, SERVICES, CITIES, IMAGES } from "@/data/content";
import { SectionHeading, CTA, FinalCTA } from "@/components/Shared";
import { RoofAccent } from "@/components/RoofAccents";
import { SEO, breadcrumbSchema } from "@/components/SEO";

export function MaterialsHubPage() {
  return (
    <div>
      <SEO
        title="Roofing Materials - 6 Systems Compared"
        description="Asphalt shingles, metal, clay tile, slate, flat (TPO/EPDM) & solar roofing in Oakland County, MI. Lifespans, pricing, pros/cons for each."
        path="/materials"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Materials", path: "/materials" }])}
      />
      <section className="bg-bg-dark text-white py-16 md:py-28 relative overflow-hidden group">
        {/* Ambient shingle-row accent that brightens on hover */}
        <svg aria-hidden="true" className="absolute -right-20 top-10 w-[560px] h-[280px] opacity-[0.05] group-hover:opacity-[0.13] transition-opacity duration-700 pointer-events-none" viewBox="0 0 560 280" fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M20 230 L90 170 L160 230 L230 170 L300 230 L370 170 L440 230 L510 170" />
          <path d="M20 200 L90 140 L160 200 L230 140 L300 200 L370 140 L440 200 L510 140" />
          <path d="M20 170 L90 110 L160 170 L230 110 L300 170 L370 110 L440 170 L510 110" />
          <path d="M20 140 L90 80 L160 140 L230 80 L300 140 L370 80 L440 140 L510 80" />
        </svg>
        <div className="container-x relative">
          <div className="overline text-brand">Roofing materials</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-7xl tracking-tight mt-3 max-w-4xl leading-[1.02]">Six roofing systems.<br />Every one installed to factory spec.</h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mt-4 md:mt-5 leading-relaxed">From architectural asphalt to standing-seam metal to natural slate - we install every major residential roofing system across Oakland County.</p>
          <div className="mt-6 md:mt-8"><CTA dark /></div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {MATERIALS.map((m, i) => {
            const accents = ["shingles", "gable", "window", "door", "vane", "skyline"];
            return (
              <Link key={m.slug} to={`/materials/${m.slug}`} className="group relative bg-white border border-ink-line p-7 hover:border-brand/40 hover:bg-bg-soft transition-all flex flex-col overflow-hidden" data-testid={`material-card-${m.slug}`}>
                <RoofAccent variant={accents[i % accents.length]} position="bottom-right" size="lg" intensity="soft" />
                <div className="w-12 h-12 bg-brand/10 text-brand flex items-center justify-center rounded-sm group-hover:bg-brand group-hover:text-white transition-colors relative"><Layers className="w-6 h-6" /></div>
                <h3 className="font-heading text-2xl font-extrabold mt-5 tracking-tight relative">{m.name}</h3>
                <p className="text-sm text-brand font-semibold mt-1 relative">{m.tagline}</p>
                <p className="text-ink-soft mt-3 leading-relaxed flex-1 relative">{m.short}</p>
                <div className="mt-4 pt-4 border-t border-ink-line/60 grid grid-cols-2 gap-3 text-xs relative">
                  <div><div className="overline text-ink-soft">Lifespan</div><div className="font-heading font-bold mt-1">{m.lifespan}</div></div>
                  <div><div className="overline text-ink-soft">Price</div><div className="font-heading font-bold mt-1 text-xs">{m.priceRange.split("per")[0].trim()}/sq</div></div>
                </div>
                <div className="mt-5 text-brand text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all relative">Learn more <ArrowRight className="w-4 h-4" /></div>
              </Link>
            );
          })}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

export function MaterialDetailPage() {
  const { slug } = useParams();
  const m = MATERIALS.find((x) => x.slug === slug);
  if (!m) {
    return (
      <div className="py-24 container-x text-center">
        <h1 className="font-heading text-4xl font-extrabold">Material not found</h1>
        <Link to="/materials" className="btn-primary mt-8">Back to Materials <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }
  return (
    <div>
      <SEO
        title={`${m.name} Roofing in Michigan`}
        description={`${m.name} installation in Oakland County, MI. Lifespan ${m.lifespan}. Installed cost ${m.priceRange}. Licensed, insured, factory-certified.`}
        path={`/materials/${m.slug}`}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Materials", path: "/materials" },
          { name: m.name, path: `/materials/${m.slug}` },
        ])}
      />
      <section className="bg-bg-dark text-white py-16 md:py-24 relative overflow-hidden group">
        {/* Dark ambient accent */}
        <svg aria-hidden="true" className="absolute -right-10 top-6 w-[420px] h-[220px] opacity-[0.05] group-hover:opacity-[0.14] transition-opacity duration-700 pointer-events-none" viewBox="0 0 420 220" fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M40 180 L130 80 L220 180" />
          <path d="M60 180 L60 210 L200 210 L200 180" />
          <rect x="115" y="150" width="30" height="40" />
          <path d="M240 180 L300 120 L360 180 L360 210 L240 210 Z" />
          <rect x="275" y="150" width="20" height="30" />
        </svg>
        <div className="container-x relative">
          <Link to="/materials" className="overline text-brand hover:underline">← All materials</Link>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-4 max-w-4xl leading-[1.02]">{m.name} in Oakland County, MI</h1>
          <p className="text-xl md:text-2xl text-brand font-semibold mt-3">{m.tagline}</p>
          <p className="text-white/80 text-base md:text-lg max-w-3xl mt-5 leading-relaxed">{m.desc}</p>
          <div className="mt-6"><CTA dark /></div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-x grid md:grid-cols-3 gap-4 md:gap-6">
          <div className="bg-bg-soft p-6 md:p-7 border border-ink-line/50">
            <Calendar className="w-6 h-6 text-brand" />
            <div className="overline text-ink-soft mt-3">Expected lifespan</div>
            <div className="font-heading text-2xl md:text-3xl font-extrabold mt-1">{m.lifespan}</div>
          </div>
          <div className="bg-bg-soft p-6 md:p-7 border border-ink-line/50">
            <DollarSign className="w-6 h-6 text-brand" />
            <div className="overline text-ink-soft mt-3">Installed price</div>
            <div className="font-heading text-xl md:text-2xl font-extrabold mt-1 leading-tight">{m.priceRange}</div>
          </div>
          <div className="bg-bg-soft p-6 md:p-7 border border-ink-line/50">
            <ShieldCheck className="w-6 h-6 text-brand" />
            <div className="overline text-ink-soft mt-3">Best for</div>
            <div className="font-heading text-base md:text-lg font-bold mt-1 leading-snug">{m.bestFor}</div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-bg-soft">
        <div className="container-x grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <div className="overline text-brand">Pros</div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-2">Why choose {m.name.toLowerCase()}?</h2>
            <ul className="mt-6 space-y-3">
              {m.pros.map((p) => (
                <li key={p} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                  <span className="text-ink-soft leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline text-ink-soft">Trade-offs</div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-2">What to consider</h2>
            <ul className="mt-6 space-y-3">
              {m.cons.map((c) => (
                <li key={c} className="flex gap-3">
                  <span className="w-5 h-5 flex-shrink-0 mt-0.5 text-ink-soft">-</span>
                  <span className="text-ink-soft leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x">
          <SectionHeading overline="Services" title={`Services that use ${m.name.toLowerCase()}`} sub="Every roofing system we install uses this material or complements it. Click through to see how we handle the full scope." />
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {SERVICES.filter((s) => ["roof-replacement", "residential-roofing", "roof-repair"].includes(s.slug)).slice(0, 3).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="bg-white border border-ink-line p-7 hover:bg-bg-soft transition-colors group">
                <h3 className="font-heading text-2xl font-extrabold tracking-tight">{s.name}</h3>
                <p className="text-ink-soft mt-2 leading-relaxed">{s.short}</p>
                <div className="mt-5 text-brand text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-4 h-4" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
